import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environment';
import {
  HotspotAssetMapping,
  HotspotAssetMappingListQuery,
  HotspotAssetMappingListResponse,
  HotspotSessionRemapResponse,
  HotspotAssetMappingUpdate
} from '../models/hotspot-asset-mapping.model';

interface ApiListResponse {
  data?: any[];
  meta?: Partial<HotspotAssetMappingListResponse['meta']>;
}

interface ApiDetailResponse {
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class HotspotAssetMappingService {
  private apiUrl = `${environment.apiUrl}/hotspot/asset-mappings`;
  private hotspotApiUrl = `${environment.apiUrl}/hotspot`;

  constructor(private http: HttpClient) {}

  getAssetMappings(query: HotspotAssetMappingListQuery): Observable<HotspotAssetMappingListResponse> {
    let params = new HttpParams()
      .set('page', query.page)
      .set('perPage', query.perPage);

    if (query.unresolved) {
      params = params.set('unresolved', true);
    }

    if (query.verified !== undefined) {
      params = params.set('verified', query.verified);
    }

    if (query.provider?.trim()) {
      params = params.set('provider', query.provider.trim());
    }

    if (query.search?.trim()) {
      params = params.set('search', query.search.trim());
    }

    return this.http.get<ApiListResponse>(this.apiUrl, { params }).pipe(
      map(response => ({
        data: (response.data || []).map(row => this.mapAssetMapping(row)),
        meta: {
          page: Number(response.meta?.page || query.page),
          perPage: Number(response.meta?.perPage || query.perPage),
          total: Number(response.meta?.total || 0),
          totalPages: Number(response.meta?.totalPages || 1)
        }
      }))
    );
  }

  getAssetMapping(id: number): Observable<HotspotAssetMapping> {
    return this.http.get<ApiDetailResponse>(`${this.apiUrl}/${id}`).pipe(
      map(response => this.mapAssetMapping(response.data || response))
    );
  }

  updateAssetMapping(id: number, payload: HotspotAssetMappingUpdate): Observable<HotspotAssetMapping> {
    return this.http.put<ApiDetailResponse | HotspotAssetMapping>(`${this.apiUrl}/${id}`, payload).pipe(
      map(response => this.mapAssetMapping((response as ApiDetailResponse).data || response))
    );
  }

  remapExistingSessions(limit: number): Observable<HotspotSessionRemapResponse> {
    const params = new HttpParams().set('limit', limit);
    return this.http.post<unknown>(`${this.hotspotApiUrl}/sessions/remap-existing`, {}, { params }).pipe(
      map(response => this.mapRemapResponse((response as ApiDetailResponse).data || response))
    );
  }

  private mapAssetMapping(row: any): HotspotAssetMapping {
    return {
      hotspotAssetMappingID: Number(row.hotspotAssetMappingID ?? row.id ?? row.hotspotAssetMappingId),
      provider: row.provider ?? 'Powerlynx',
      nasIdentifier: row.nasIdentifier ?? '',
      externalLocationID: row.externalLocationID ?? row.externalLocationId ?? row.ExternalLocationID ?? null,
      externalHotspotID: row.externalHotspotID ?? row.externalHotspotId ?? row.ExternalHotspotID ?? null,
      operatorCode: row.operatorCode ?? row.OperatorCode ?? row.operator_code ?? null,
      vehicleCode: row.vehicleCode ?? row.VehicleCode ?? row.vehicle_code ?? null,
      organisationID: row.organisationID ?? row.organisationId ?? null,
      organisationName: row.organisationName,
      vehicleID: row.vehicleID ?? row.vehicleId ?? null,
      vehicleIdentificationNumber: row.vehicleIdentificationNumber,
      vehicleRegistration: row.vehicleRegistration,
      vehicleLabel: row.vehicleLabel,
      isVerified: Boolean(row.isVerified),
      firstSeenAt: row.firstSeenAt,
      lastSeenAt: row.lastSeenAt
    };
  }

  private mapRemapResponse(row: any): HotspotSessionRemapResponse {
    return {
      requestedLimit: Number(row.requestedLimit ?? row.RequestedLimit ?? 0),
      reviewed: Number(row.reviewed ?? row.Reviewed ?? 0),
      remappedSessions: Number(row.remappedSessions ?? row.RemappedSessions ?? 0),
      createdAssetMappings: Number(row.createdAssetMappings ?? row.CreatedAssetMappings ?? 0),
      errors: Number(row.errors ?? row.Errors ?? 0),
      errorDetails: row.errorDetails ?? row.ErrorDetails ?? []
    };
  }
}
